/**
 * Blank-line codec for markdown persisted by the memory RichTextEditor.
 *
 * Storage keeps standard-markdown semantics so notes written server-side by the
 * agent (and pre-existing notes) render tight: a single blank line ("\n\n") is
 * just a block separator, NOT a visual gap. Only blank lines beyond the first in
 * a run are real, user-intended empty lines.
 *
 * The editor (Lexical with shouldPreserveNewLines) uses the opposite convention:
 * every empty line is a real empty paragraph, and adjacent blocks sit on
 * consecutive lines. These two functions translate between the conventions:
 *
 *   editor "a\nb"    <-> storage "a\nb"      (adjacent blocks)
 *   editor "a\n\nb"  <-> storage "a\n\n\nb"  (one intentional blank line)
 *   storage "a\n\nb"  -> editor "a\nb"       (agent/legacy block separator)
 *
 * decode(encode(x)) === x, so user content round-trips losslessly. Runs of
 * blank lines inside fenced code blocks are left untouched.
 */

const FENCE_RE = /^\s*(```|~~~)/;

const shiftBlankRuns = (markdown: string, delta: 1 | -1): string => {
  const out: string[] = [];
  let inFence = false;
  let run = 0;
  const flushRun = () => {
    const count = run > 0 ? run + delta : 0;
    for (let i = 0; i < count; i++) {
      out.push("");
    }
    run = 0;
  };
  for (const line of markdown.split("\n")) {
    if (inFence) {
      out.push(line);
      if (FENCE_RE.test(line)) {
        inFence = false;
      }
    } else if (line.trim() === "") {
      run++;
    } else {
      flushRun();
      out.push(line);
      if (FENCE_RE.test(line)) {
        inFence = true;
      }
    }
  }
  flushRun();
  return out.join("\n");
};

/** Editor markdown -> storage markdown: widen each blank-line run by one. */
export const encodeBlankLines = (markdown: string): string => shiftBlankRuns(markdown, 1);

/** Storage markdown -> editor markdown: shrink each blank-line run by one. */
export const decodeBlankLines = (markdown: string): string => shiftBlankRuns(markdown, -1);
